import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { format, addDays, subDays } from "date-fns";
import { tr } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, RefreshCw, Search, X, Plus, FileSpreadsheet,
  Check, ChevronDown, ChevronUp, Phone, Ship, BusFront,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getToken, isTokenExpiring, refreshAccessToken, authHeaders } from "../lib/authRefresh";
import { exportSheetToExcel, exportTransferListToExcel } from "../lib/exportExcel";

const API = window.location.origin;

// Bos/manuel rez ekleme formu alanlari (col.key -> etiket)
const MANUAL_FORM = [
  { key: "isim", label: "İsim", ph: "Müşteri adı" },
  { key: "saat", label: "Saat", ph: "20:30" },
  { key: "pax", label: "Pax", ph: "2", type: "number" },
  { key: "telefon", label: "Telefon", ph: "+90…" },
  { key: "otel_adi", label: "Otel", ph: "Otel adı" },
  { key: "acenta", label: "Acenta", ph: "Acenta" },
  { key: "kod", label: "KOD", ph: "Rezervasyon kodu" },
  { key: "opsiyon", label: "Opsiyon", ph: "Ürün / opsiyon" },
];
// Karti genisletince duzenlenebilir operasyon alanlari (layout'a gore uygun olanlar gosterilir)
const EDITABLE_KEYS = ["masa_no", "alt_no", "masa_no_2", "garson", "aciklama", "yildiz"];
// Genisletince duzenlenebilir görünüm alanları (HER satırda — override)
const DISPLAY_EDIT = [
  { key: "isim", label: "İsim" }, { key: "saat", label: "Saat" },
  { key: "pax", label: "Pax", type: "number" }, { key: "telefon", label: "Telefon" },
  { key: "otel_adi", label: "Otel" }, { key: "acenta", label: "Acenta" },
  { key: "kod", label: "KOD" }, { key: "opsiyon", label: "Opsiyon" },
];

export default function LiveSheetMobile() {
  const auth = useAuth();
  const { canManage } = auth;
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState(searchParams.get("date") || format(new Date(), "yyyy-MM-dd"));
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") ? Number(searchParams.get("category")) : null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [collapsed, setCollapsed] = useState({});   // boat_id -> true (katlanmis)
  const [expanded, setExpanded] = useState({});      // row_id -> true (detay acik)
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [manualBoat, setManualBoat] = useState(null); // acik ekleme formunun boat_id'si
  const [busy, setBusy] = useState(false);
  // Tablet (>=768px) -> Excel benzeri TABLO; telefon (<768) -> kart görünümü
  const [isTablet, setIsTablet] = useState(typeof window !== "undefined" && window.innerWidth >= 768);
  useEffect(() => {
    const onR = () => setIsTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const token = localStorage.getItem("auth_token");
  const dateRef = useRef(date);
  useEffect(() => { dateRef.current = date; }, [date]);

  // Garson tarih penceresi (bugun ±2 gun)
  const role = (auth.role || "").toLowerCase();
  const isGarson = !!auth.user && role !== "admin" && role !== "super_admin" && role !== "manager";
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const minDate = isGarson ? format(subDays(new Date(), 2), "yyyy-MM-dd") : null;
  const maxDate = isGarson ? format(addDays(new Date(), 2), "yyyy-MM-dd") : null;

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (isTokenExpiring(getToken())) { await refreshAccessToken(); }  // token dolmak uzereyse yenile (24h sonra "yuklenemez" onlenir)
      let url = `${API}/api/sheet/fortune/${dateRef.current}?group=1&`;
      if (activeCategory != null) url += `category_id=${activeCategory}&`;
      // Gecici origin/Cloudflare hatasi (520-524, 5xx, ag kopmasi) -> kisa backoff ile 3 kez dene, kullaniciyi rahatsiz etme.
      let res = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        res = await axios.get(url, { headers: authHeaders(), validateStatus: () => true, timeout: 25000 });
        const transient = res.status === 0 || res.status === 429 || (res.status >= 500 && res.status <= 599);
        if (!transient) break;
        if (attempt < 2) await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
      }
      if (res.status === 403) { setError("Bu tarihi görüntüleme yetkiniz yok."); setData(null); return; }
      if (res.status !== 200) { setError(res.data?.detail || "Yüklenemedi"); return; }
      setData(res.data);
    } catch (e) {
      setError("Bağlantı hatası — tekrar deneyin");
    } finally { setLoading(false); }
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [date, activeCategory, fetchData]);

  // URL senkron
  useEffect(() => {
    const p = {};
    if (date) p.date = date;
    if (activeCategory != null) p.category = String(activeCategory);
    setSearchParams(p, { replace: true });
  }, [date, activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket canli senkron -> mesajda debounce refetch
  const refetchTimer = useRef(null);
  useEffect(() => {
    if (!getToken()) return;
    let ws; let closed = false; let retry;
    const scheduleRefetch = () => {
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => { fetchData(); }, 700);
    };
    const connect = async () => {
      try {
        let tok = getToken();
        if (isTokenExpiring(tok)) { const nt = await refreshAccessToken(); if (nt) tok = nt; }
        if (!tok || closed) return;
        const wsUrl = `${API.replace(/^http/, "ws")}/api/ws/sheet?date=${date}&token=${encodeURIComponent(tok)}`;
        ws = new WebSocket(wsUrl);
        ws.onopen = () => setConnected(true);
        ws.onclose = () => { setConnected(false); if (!closed) retry = setTimeout(connect, 3000); };
        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (["cell_update", "row_update", "row", "reorder"].includes(msg.type)) scheduleRefetch();
          } catch { /* noop */ }
        };
      } catch { /* noop */ }
    };
    connect();
    return () => { closed = true; clearTimeout(retry); clearTimeout(refetchTimer.current); try { ws && ws.close(); } catch { /* noop */ } };
  }, [date, token, fetchData]);

  const changeDate = (d) => {
    let nd = format(d, "yyyy-MM-dd");
    if (minDate && nd < minDate) { toast.info("Sadece bugün ±2 gün görüntülenebilir"); nd = minDate; }
    if (maxDate && nd > maxDate) { toast.info("Sadece bugün ±2 gün görüntülenebilir"); nd = maxDate; }
    setDate(nd);
  };

  // ---- Mutasyonlar ----
  const doCheckin = async (row) => {
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/api/sheet/row/${row.row_id}/checkin`,
        { checked_in: !row.checked_in }, { headers: authHeaders(), validateStatus: () => true });
      if (res.status === 200) { row.checked_in = !row.checked_in; setData((d) => ({ ...d })); }
      else toast.error(res.data?.detail || "İşaretlenemedi");
    } catch { toast.error("Ağ hatası"); } finally { setBusy(false); }
  };

  const doAssign = async (row, boatId) => {
    if (!boatId || boatId === row.boat_id) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/sheet/bulk-move`,
        { row_ids: [row.row_id], target_boat_id: boatId }, { headers: authHeaders(), validateStatus: () => true });
      if (res.status === 200 && (res.data?.moved || 0) > 0) { toast.success("Tekne değiştirildi"); await fetchData(); }
      else toast.error(res.data?.detail || "Taşınamadı");
    } catch { toast.error("Ağ hatası"); } finally { setBusy(false); }
  };

  const doCancel = async (row) => {
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/api/sheet/row/${row.row_id}/cancel`,
        { cancelled: true }, { headers: authHeaders(), validateStatus: () => true });
      if (res.status === 200) { toast.success("Rezervasyon iptal edildi"); await fetchData(); }
      else toast.error(res.data?.detail || "İptal edilemedi");
    } catch { toast.error("Ağ hatası"); } finally { setBusy(false); }
  };

  const saveField = async (row, col, value) => {
    const cur = row.values?.[col.key] ?? "";
    if (String(cur) === String(value)) return;
    const payload = col.type === "number" ? (value === "" ? null : Number(value)) : value;
    let expVer = row.version;
    try {
      for (let t = 0; t < 3; t++) {
        const res = await axios.patch(`${API}/api/sheet/row/${row.row_id}/cell`,
          { field: col.field, value: payload, expected_version: expVer },
          { headers: authHeaders(), validateStatus: () => true });
        if (res.status === 200) {
          row.values[col.key] = value; row.version = res.data?.version ?? ((row.version || 1) + 1); setData((d) => ({ ...d })); return;
        }
        if (res.status === 409 && res.data?.current_version && t < 2) { expVer = res.data.current_version; row.version = expVer; continue; }
        if (res.status === 409) { toast.warning("Başkası değiştirdi — yenilendi"); await fetchData(); return; }
        toast.error(res.data?.detail || "Kaydedilemedi"); return;
      }
    } catch { toast.error("Ağ hatası"); }
  };

  // Menü kovaları (M1/M2/VIP...) -> menu-cell endpoint (operation_rows.menu_cells)
  const saveMenu = async (row, col, value) => {
    const cur = row.values?.[col.key] ?? "";
    if (String(cur) === String(value)) return;
    const numVal = value === "" ? null : Number(value);
    let expVer = row.version;
    try {
      for (let t = 0; t < 3; t++) {
        const res = await axios.patch(`${API}/api/sheet/row/${row.row_id}/menu-cell`,
          { key: col.field, value: numVal, expected_version: expVer },
          { headers: authHeaders(), validateStatus: () => true });
        if (res.status === 200) {
          row.values[col.key] = value; row.version = res.data?.version ?? ((row.version || 1) + 1); setData((d) => ({ ...d })); return;
        }
        if (res.status === 409 && res.data?.current_version && t < 2) { expVer = res.data.current_version; row.version = expVer; continue; }
        if (res.status === 409) { toast.warning("Başkası değiştirdi — yenilendi"); await fetchData(); return; }
        toast.error(res.data?.detail || "Kaydedilemedi"); return;
      }
    } catch { toast.error("Ağ hatası"); }
  };

  // Görünüm alanları (isim/otel/pax/kod/telefon/acenta/opsiyon/saat) — HER satırda düzenlenebilir (override)
  const saveDisplay = async (row, key, value) => {
    const cur = row.values?.[key] ?? "";
    if (String(cur) === String(value)) return;
    try {
      const res = await axios.post(`${API}/api/sheet/manual/cell`,
        { row_id: row.row_id, booking_date: date, field: key, value },
        { headers: authHeaders(), validateStatus: () => true });
      if (res.status === 200) { row.values[key] = value; setData((d) => ({ ...d })); }
      else toast.error(res.data?.detail || "Kaydedilemedi");
    } catch { toast.error("Ağ hatası"); }
  };

  const createManual = async (boatId, fields) => {
    const entries = Object.entries(fields).filter(([, v]) => String(v ?? "").trim() !== "");
    if (!entries.length) { toast.error("En az bir alan girin"); return; }
    setBusy(true);
    try {
      let rowId = null;
      for (const [field, value] of entries) {
        const body = rowId
          ? { row_id: rowId, booking_date: date, field, value }
          : { booking_date: date, boat_id: boatId, field, value };
        const res = await axios.post(`${API}/api/sheet/manual/cell`, body, { headers: authHeaders(), validateStatus: () => true });
        if (res.status !== 200) { toast.error(res.data?.detail || "Eklenemedi"); return; }
        if (!rowId) rowId = res.data.row_id;
      }
      toast.success("Rezervasyon eklendi");
      setManualBoat(null);
      await fetchData();
    } catch { toast.error("Ağ hatası — eklenemedi"); } finally { setBusy(false); }
  };

  // ---- Veriyi bolumlere ayir (group_headers sirasina gore) ----
  const columns = data?.columns || [];
  const colByKey = {}; columns.forEach((c) => { colByKey[c.key] = c; });
  const editableCols = EDITABLE_KEYS.map((k) => colByKey[k]).filter((c) => c && c.editable && !c.hidden && (c.key !== "yildiz" || auth.isSuperAdmin));
  const boatOptions = (data?.boats || []).map((b) => ({ id: b.id, name: b.name }));

  const headersSorted = [...(data?.group_headers || [])].sort((a, b) => a.r - b.r);
  const rows = data?.rows || [];
  const q = search.trim().toLowerCase();
  const matchRow = (row) => {
    if (!q) return true;
    const vals = row.values || {};
    return Object.values(vals).some((v) => v != null && String(v).toLowerCase().includes(q));
  };
  const sections = headersSorted.map((h, i) => {
    const nextR = i + 1 < headersSorted.length ? headersSorted[i + 1].r : Infinity;
    const secRows = rows.filter((r) => r.r > h.r && r.r < nextR).filter(matchRow);
    return { ...h, rows: secRows };
  }).filter((s) => s.rows.length > 0 || (!q && s.boat_id !== "__cancelled__"));

  const fmtDate = (() => { try { return format(new Date(date + "T00:00:00"), "d MMM EEE", { locale: tr }); } catch { return date; } })();

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="ls-mobile-root" style={{ height: "100dvh", overflow: "hidden" }}>
      {/* Ust bar */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-bold text-slate-800 text-base truncate">Canlı Defter</span>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="ls-m-live">
              <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
              {connected ? "Canlı" : "Bağlanıyor"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="ls-m-search-toggle" onClick={() => setShowSearch((v) => !v)}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200">
              <Search size={18} />
            </button>
            <button data-testid="ls-m-refresh" onClick={fetchData}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200">
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="ls-m-export-excel"
              onClick={async () => { try { toast.loading("Excel hazırlanıyor…", { id: "xls" }); await exportSheetToExcel({ date, activeCategory, data }); toast.success("Excel indirildi", { id: "xls" }); } catch { toast.error("Excel dışa aktarılamadı", { id: "xls" }); } }}
              title="Defteri Excel (.xlsx) olarak indir"
              className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 active:bg-emerald-100">
              <FileSpreadsheet size={18} />
            </button>
            <button data-testid="ls-m-export-transfer"
              onClick={async () => { try { toast.loading("Transfer listesi hazırlanıyor…", { id: "xls-t" }); await exportTransferListToExcel({ date, activeCategory, data }); toast.success("Transfer listesi indirildi", { id: "xls-t" }); } catch { toast.error("Transfer listesi dışa aktarılamadı", { id: "xls-t" }); } }}
              title="Transfer listesi: sadece transferli rezler (Kendi Gelir hariç)"
              className="p-2 rounded-lg text-sky-600 hover:bg-sky-50 active:bg-sky-100">
              <BusFront size={18} />
            </button>
          </div>
        </div>

        {/* Tarih gezinme */}
        <div className="flex items-center justify-between px-3 pb-2">
          <button data-testid="ls-m-date-prev" onClick={() => changeDate(subDays(new Date(date + "T00:00:00"), 1))}
            className="p-2 rounded-lg bg-slate-100 text-slate-700 active:bg-slate-200"><ChevronLeft size={18} /></button>
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-800 text-sm" data-testid="ls-m-date">{fmtDate}</span>
            {date !== todayStr && (
              <button data-testid="ls-m-today" onClick={() => changeDate(new Date())}
                className="text-[11px] font-semibold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 active:bg-indigo-200">Bugün</button>
            )}
          </div>
          <button data-testid="ls-m-date-next" onClick={() => changeDate(addDays(new Date(date + "T00:00:00"), 1))}
            className="p-2 rounded-lg bg-slate-100 text-slate-700 active:bg-slate-200"><ChevronRight size={18} /></button>
        </div>

        {/* Arama */}
        {showSearch && (
          <div className="px-3 pb-2">
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
              <Search size={15} className="text-slate-400 flex-shrink-0" />
              <input data-testid="ls-m-search-input" autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="İsim, otel, kod, telefon…" className="flex-1 bg-transparent outline-none text-sm text-slate-800" />
              {search && <button onClick={() => setSearch("")} className="text-slate-400"><X size={15} /></button>}
            </div>
          </div>
        )}

        {/* Kategori sekmeleri */}
        <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto no-scrollbar" data-testid="ls-m-categories">
          {(data?.categories || []).map((cat) => {
            const active = activeCategory === cat.id;
            return (
              <button key={cat.id} data-testid={`ls-m-cat-${cat.id}`} onClick={() => setActiveCategory(cat.id)}
                className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300"}`}>
                {cat.name} <span className="opacity-70">({cat.count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Icerik */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-2 py-2 pb-24" data-testid="ls-m-body" style={{ WebkitOverflowScrolling: "touch" }}>
        {loading && !data && <div className="text-center text-slate-400 py-12 text-sm">Yükleniyor…</div>}
        {error && <div className="text-center text-red-500 py-12 text-sm" data-testid="ls-m-error">{error}</div>}
        {!loading && !error && sections.length === 0 && (
          <div className="text-center text-slate-400 py-12 text-sm">Kayıt yok.</div>
        )}

        {isTablet && !loading && !error && sections.length > 0 && (
          <TableView sections={sections} columns={columns} boatOptions={boatOptions}
            canManage={canManage} isGarson={isGarson} isSuperAdmin={!!auth.isSuperAdmin} busy={busy} garsonNames={data?.garson_names || []}
            onCheckin={doCheckin} onAssign={doAssign} onCancel={doCancel}
            onSaveField={saveField} onSaveDisplay={saveDisplay} onSaveMenu={saveMenu}
            manualBoat={manualBoat} setManualBoat={setManualBoat} onCreateManual={createManual} />
        )}

        {!isTablet && sections.map((sec) => {
          const isCancelled = sec.boat_id === "__cancelled__";
          const col = collapsed[sec.boat_id];
          return (
            <div key={`sec-${sec.r}-${sec.boat_id}`} className="mb-3" data-testid={`ls-m-section-${sec.boat_id}`}>
              {/* Banner */}
              <div className="flex items-center justify-between rounded-t-xl px-3 py-2 shadow-sm"
                style={{ backgroundColor: sec.bg || "#E2E8F0" }}>
                <button onClick={() => setCollapsed((c) => ({ ...c, [sec.boat_id]: !c[sec.boat_id] }))}
                  className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`ls-m-section-toggle-${sec.boat_id}`}>
                  {col ? <ChevronDown size={16} className="text-slate-700 flex-shrink-0" /> : <ChevronUp size={16} className="text-slate-700 flex-shrink-0" />}
                  <span className="font-bold text-slate-800 text-sm truncate">{sec.name}</span>
                  <span className="text-[11px] font-semibold text-slate-600 flex-shrink-0">· {sec.count} rez · {sec.pax} kişi</span>
                </button>
                {canManage && !isCancelled && (
                  <button data-testid={`ls-m-add-${sec.boat_id}`} onClick={() => { setManualBoat(sec.boat_id); setCollapsed((c) => ({ ...c, [sec.boat_id]: false })); }}
                    className="flex-shrink-0 flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-full active:bg-emerald-700">
                    <Plus size={12} /> Ekle
                  </button>
                )}
              </div>

              {!col && (
                <div className="space-y-2 mt-2">
                  {/* Manuel ekleme formu */}
                  {manualBoat === sec.boat_id && (
                    <ManualForm boatId={sec.boat_id} busy={busy}
                      onCancel={() => setManualBoat(null)} onSubmit={(f) => createManual(sec.boat_id, f)} />
                  )}
                  {sec.rows.map((row) => (
                    <Card key={row.row_id || row.r} row={row} isCancelled={isCancelled}
                      editableCols={editableCols} boatOptions={boatOptions} canManage={canManage}
                      expanded={!!expanded[row.row_id]} busy={busy}
                      onToggleExpand={() => setExpanded((e) => ({ ...e, [row.row_id]: !e[row.row_id] }))}
                      onCheckin={() => doCheckin(row)} onAssign={(bid) => doAssign(row, bid)}
                      onCancel={() => doCancel(row)}
                      onSaveField={(cc, v) => saveField(row, cc, v)}
                      onSaveDisplay={(k, v) => saveDisplay(row, k, v)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Rezervasyon kart bileseni ---
function Card({ row, isCancelled, editableCols, boatOptions, canManage, expanded, busy, onToggleExpand, onCheckin, onAssign, onCancel, onSaveField, onSaveDisplay }) {
  const v = row.values || {};
  const isManual = row.source === "manual";
  const tags = row.tags || [];
  const phone = v.telefon;
  const bg = row.checked_in ? "#ecfdf5" : (isCancelled ? "#fef2f2" : (isManual ? "#eff6ff" : "#ffffff"));
  return (
    <div data-testid={`ls-m-card-${row.row_id}`} className="rounded-xl border border-slate-200 shadow-sm overflow-hidden"
      style={{ backgroundColor: bg, opacity: isCancelled ? 0.85 : 1 }}>
      <div className="p-3" onClick={onToggleExpand}>
        {/* Ust satir */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            {v.saat && <span className="font-bold text-slate-900 text-sm tabular-nums">{v.saat}</span>}
            <span className="inline-flex items-center text-[11px] font-bold text-white bg-slate-700 rounded-full px-2 py-0.5">{v.pax || 0} kişi</span>
            {isManual && <span className="text-[10px] font-bold text-blue-700 bg-blue-100 rounded px-1.5 py-0.5">MANUEL</span>}
            {row.locked && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5" title="Elle atandı — otomatik dağıtım dokunmaz">🔒</span>}
            {row.checked_in && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700"><Check size={11} /> Geldi</span>}
          </div>
          {expanded ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />}
        </div>
        {/* İsim */}
        {v.isim && <div className="font-semibold text-slate-800 text-sm truncate">{v.isim}</div>}
        {/* Otel · Acenta */}
        {(v.otel_adi || v.acenta) && (
          <div className="text-xs text-slate-500 truncate">{[v.otel_adi, v.acenta].filter(Boolean).join("  ·  ")}</div>
        )}
        {/* Etiketler */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {tags.map((t, i) => (
              <span key={i} className="text-[10px] font-bold text-white rounded px-1.5 py-0.5" style={{ backgroundColor: t.bg || "#64748b" }}>{t.label}</span>
            ))}
          </div>
        )}
        {/* Ekstra + KOD */}
        <div className="flex items-center justify-between gap-2 mt-1.5">
          {v.ekstra ? <span className="text-[11px] font-semibold text-amber-700 truncate">{v.ekstra}</span> : <span />}
          {v.kod && <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">{v.kod}</span>}
        </div>
      </div>

      {/* Aksiyon satiri */}
      <div className="flex items-stretch border-t border-slate-100 divide-x divide-slate-100" onClick={(e) => e.stopPropagation()}>
        <button data-testid={`ls-m-checkin-${row.row_id}`} disabled={busy || isCancelled} onClick={onCheckin}
          className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-semibold active:bg-slate-50 disabled:opacity-40 ${row.checked_in ? "text-slate-500" : "text-emerald-700"}`}>
          <Check size={14} /> {row.checked_in ? "Geri Al" : "Geldi"}
        </button>
        {phone && (
          <a data-testid={`ls-m-call-${row.row_id}`} href={`tel:${String(phone).replace(/[^\d+]/g, "")}`}
            className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-semibold text-blue-700 active:bg-slate-50">
            <Phone size={14} /> Ara
          </a>
        )}
        {canManage && !isCancelled && (
          <label data-testid={`ls-m-boat-wrap-${row.row_id}`} className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-semibold text-indigo-700 active:bg-slate-50 relative">
            <Ship size={14} /> Tekne
            <select data-testid={`ls-m-boat-${row.row_id}`} value={row.boat_id || ""}
              onChange={(e) => { const val = e.target.value; if (val === "__cancel__") onCancel(); else onAssign(Number(val)); }}
              className="absolute inset-0 opacity-0 w-full h-full">
              <option value="" disabled>Tekne seç</option>
              {boatOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              <option value="__cancel__">❌ İptal Et</option>
            </select>
          </label>
        )}
      </div>

      {/* Genisletilmis: düzenlenebilir alanlar (görünüm + operasyon) */}
      {expanded && (
        <div className="px-3 py-3 bg-slate-50 border-t border-slate-100 grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
          {DISPLAY_EDIT.map((c) => (
            <label key={c.key} className={`text-[11px] text-slate-500 flex flex-col gap-1 ${c.key === "opsiyon" ? "col-span-2" : ""}`}>
              {c.label}
              <input data-testid={`ls-m-disp-${c.key}-${row.row_id}`} defaultValue={v[c.key] ?? ""}
                type={c.type === "number" ? "number" : "text"}
                onBlur={(e) => onSaveDisplay(c.key, e.target.value)}
                className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-indigo-400" />
            </label>
          ))}
          {editableCols.map((c) => (
            <label key={c.key} className="text-[11px] text-slate-500 flex flex-col gap-1">
              {c.label}
              <input data-testid={`ls-m-field-${c.key}-${row.row_id}`} defaultValue={v[c.key] ?? ""}
                type={c.type === "number" ? "number" : "text"}
                onBlur={(e) => onSaveField(c, e.target.value)}
                className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-indigo-400" />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Manuel ekleme formu ---
function ManualForm({ busy, onCancel, onSubmit }) {
  const [f, setF] = useState({});
  const set = (k, val) => setF((p) => ({ ...p, [k]: val }));
  return (
    <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-3" data-testid="ls-m-manual-form">
      <div className="grid grid-cols-2 gap-2 mb-2">
        {MANUAL_FORM.map((mf) => (
          <label key={mf.key} className="text-[11px] text-slate-500 flex flex-col gap-1">
            {mf.label}
            <input data-testid={`ls-m-manual-${mf.key}`} type={mf.type || "text"} placeholder={mf.ph}
              value={f[mf.key] || ""} onChange={(e) => set(mf.key, e.target.value)}
              className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-emerald-400" />
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button data-testid="ls-m-manual-save" disabled={busy} onClick={() => onSubmit(f)}
          className="flex-1 bg-emerald-600 text-white text-sm font-bold py-2 rounded-lg active:bg-emerald-700 disabled:opacity-50">Kaydet</button>
        <button data-testid="ls-m-manual-cancel" onClick={onCancel}
          className="px-4 bg-white border border-slate-300 text-slate-600 text-sm font-semibold py-2 rounded-lg active:bg-slate-100">İptal</button>
      </div>
    </div>
  );
}


// --- Tablet: Excel benzeri TABLO görünümü (sabit başlık, dokununca hücre düzenleme) ---
const T_DISPLAY = [
  { key: "saat", label: "Saat", w: 60, type: "text" },
  { key: "isim", label: "İsim", w: 150 },
  { key: "pax", label: "Pax", w: 46, type: "number", align: "center" },
  { key: "otel_adi", label: "Otel", w: 160 },
  { key: "acenta", label: "Acenta", w: 84 },
  { key: "kod", label: "KOD", w: 120 },
  { key: "opsiyon", label: "Opsiyon", w: 210 },
];
const T_OP_KEYS = ["masa_no", "alt_no", "garson", "aciklama"];
const FLOOR_OPTS_M = { "MASAL": ["1.KAT", "2.KAT"], "MEGA": ["1.KAT", "2.KAT"], "LÜFER 6": ["1.KAT", "2.KAT", "3.KAT"] };
const MASA_NO_OPTS = Array.from({ length: 90 }, (_, i) => String(i + 1));

function TableView({ sections, columns, boatOptions, canManage, isGarson = false, isSuperAdmin = false, busy, garsonNames = [], onCheckin, onAssign, onCancel, onSaveField, onSaveDisplay, onSaveMenu, manualBoat, setManualBoat, onCreateManual }) {
  const [editing, setEditing] = useState(null); // `${row_id}:${key}`
  // Garson: masa_no / alt_no / garson / aciklama duzenleyebilir; Manager+ hepsi.
  const GARSON_T_FIELDS = new Set(["masa_no", "alt_no", "garson", "aciklama"]);
  const canEditCell = (key) => key === "yildiz" ? isSuperAdmin : (canManage || (isGarson && GARSON_T_FIELDS.has(key)));
  const colByKey = {}; (columns || []).forEach((c) => { colByKey[c.key] = c; });
  const opCols = T_OP_KEYS.map((k) => colByKey[k]).filter((c) => c && c.editable);
  const boatNameById = {}; (boatOptions || []).forEach((b) => { boatNameById[b.id] = (b.name || "").trim(); });
  const menuCols = (columns || []).filter((c) => c.source === "menu"); // M1/M2/VIP... (tam Excel defteri)
  const cols = [
    ...T_DISPLAY.map((c) => ({ ...c, kind: "display" })),
    { key: "ekstra", label: "Ekstra", w: 150, kind: "ro" },
    ...menuCols.map((c) => ({ key: c.key, label: c.label, w: 44, kind: "menu", col: c, type: "number", align: "center", menu: true })),
    ...opCols.filter((c) => !c.hidden).map((c) => ({ key: c.key, label: c.label, w: 90, kind: "op", col: c, type: c.type })),
  ].filter((c) => !(colByKey[c.key] && colByKey[c.key].hidden));
  const totalCols = cols.length + 2; // + Tekne + Geldi

  const EditCell = ({ row, cdef, dup }) => {
    const eKey = `${row.row_id}:${cdef.key}`;
    const val = row.values?.[cdef.key] ?? "";
    const save = (v) => {
      setEditing(null);
      if (String(v) === String(val)) return;
      if (cdef.kind === "menu") onSaveMenu(row, cdef.col, v);
      else if (cdef.kind === "op") onSaveField(row, cdef.col, v);
      else onSaveDisplay(row, cdef.key, v);
    };
    const bname = (boatNameById[row.boat_id] || "").toUpperCase();
    const isFloor = cdef.key === "masa_no" && FLOOR_OPTS_M[bname];
    const isMasaNo = cdef.key === "alt_no";
    const isGarsonCell = cdef.key === "garson";
    if (editing === eKey) {
      const commonCls = "w-full h-full px-2 py-2 text-[13px] text-slate-900 bg-yellow-50 outline-none ring-2 ring-indigo-400";
      let control;
      if (isFloor) {
        control = (
          <select autoFocus defaultValue={val} onChange={(e) => save(e.target.value)} onBlur={(e) => save(e.target.value)} className={commonCls}>
            <option value=""></option>
            {FLOOR_OPTS_M[bname].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      } else if (isMasaNo) {
        control = (
          <select autoFocus defaultValue={String(val)} onChange={(e) => save(e.target.value)} onBlur={(e) => save(e.target.value)} className={`${commonCls} text-center`}>
            <option value=""></option>
            {MASA_NO_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      } else if (isGarsonCell) {
        control = (
          <>
            <input autoFocus list="ls-t-garson-list" defaultValue={val}
              onBlur={(e) => save(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditing(null); }}
              className={commonCls} />
            <datalist id="ls-t-garson-list">{garsonNames.map((n) => <option key={n} value={n} />)}</datalist>
          </>
        );
      } else {
        control = (
          <input autoFocus type={cdef.type || "text"} defaultValue={val}
            onBlur={(e) => save(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditing(null); }}
            className={`${commonCls} ${cdef.align === "center" ? "text-center" : ""}`} />
        );
      }
      return (
        <td className="border-r border-slate-200 p-0" style={{ minWidth: cdef.w }}>{control}</td>
      );
    }
    return (
      <td onClick={() => canEditCell(cdef.key) && setEditing(eKey)}
        className={`border-r border-slate-200 px-2 py-2 text-[13px] truncate ${cdef.menu ? "font-semibold text-indigo-700 bg-indigo-50/40" : "text-slate-800"} ${dup ? "bg-green-400 text-white font-bold" : ""} ${canEditCell(cdef.key) ? "cursor-pointer active:bg-indigo-50" : ""} ${cdef.align === "center" ? "text-center" : ""}`}
        style={{ minWidth: cdef.w, maxWidth: cdef.menu ? cdef.w : cdef.w * 1.6 }} title={String(val)}>
        {String(val)}
      </td>
    );
  };

  return (
    <div className="overflow-auto rounded-lg border border-slate-200 bg-white" data-testid="ls-t-table-wrap"
      style={{ WebkitOverflowScrolling: "touch", maxHeight: "calc(100dvh - 190px)" }}>
      <table className="border-collapse" style={{ minWidth: "max-content" }} data-testid="ls-t-table">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-800 text-white text-[11px] uppercase tracking-wide">
            {cols.map((c) => (
              <th key={c.key} className="border-r border-slate-700 px-2 py-2 text-left font-semibold" style={{ minWidth: c.w }}>{c.label}</th>
            ))}
            <th className="border-r border-slate-700 px-2 py-2 text-center font-semibold" style={{ minWidth: 120 }}>Tekne</th>
            <th className="px-2 py-2 text-center font-semibold" style={{ minWidth: 74 }}>Geldi</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((sec) => {
            const isCancelled = sec.boat_id === "__cancelled__";
            // Ayni teknede AYNI Masa No (alt_no) -> yesil isaretlenecek degerler
            const _cnt = {};
            sec.rows.forEach((r) => { const v = String(r.values?.alt_no ?? "").trim(); if (v) _cnt[v] = (_cnt[v] || 0) + 1; });
            const dupSet = new Set(Object.keys(_cnt).filter((k) => _cnt[k] > 1));
            return (
              <React.Fragment key={`t-sec-${sec.r}-${sec.boat_id}`}>
                <tr data-testid={`ls-t-group-${sec.boat_id}`} style={{ backgroundColor: sec.bg || "#E2E8F0" }}>
                  <td colSpan={totalCols} className="px-3 py-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800 text-[13px]">{sec.name} <span className="font-semibold text-slate-600 text-[11px]">· {sec.count} rez · {sec.pax} kişi</span></span>
                      {canManage && !isCancelled && (
                        <button data-testid={`ls-t-add-${sec.boat_id}`} onClick={() => setManualBoat(manualBoat === sec.boat_id ? null : sec.boat_id)}
                          className="flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full active:bg-emerald-700"><Plus size={12} /> Ekle</button>
                      )}
                    </div>
                  </td>
                </tr>
                {manualBoat === sec.boat_id && (
                  <tr><td colSpan={totalCols} className="p-2 bg-slate-50">
                    <ManualForm boatId={sec.boat_id} busy={busy} onCancel={() => setManualBoat(null)} onSubmit={(f) => onCreateManual(sec.boat_id, f)} />
                  </td></tr>
                )}
                {sec.rows.map((row) => (
                  <tr key={row.row_id || row.r} data-testid={`ls-t-row-${row.row_id}`}
                    className={`border-b border-slate-100 ${row.checked_in ? "bg-emerald-50" : (row.cancelled ? "bg-red-50" : (row.source === "manual" ? "bg-blue-50" : "bg-white"))}`}>
                    {cols.map((c) => (
                      c.kind === "ro"
                        ? <td key={c.key} className="border-r border-slate-200 px-2 py-2 text-[12px] text-amber-700 truncate" style={{ minWidth: c.w, maxWidth: c.w * 1.6 }} title={String(row.values?.[c.key] ?? "")}>{String(row.values?.[c.key] ?? "")}</td>
                        : <EditCell key={c.key} row={row} cdef={c} dup={c.key === "alt_no" && dupSet.has(String(row.values?.alt_no ?? "").trim())} />
                    ))}
                    <td className="border-r border-slate-200 px-1 py-1 text-center" style={{ minWidth: 120 }}>
                      {canManage && !isCancelled ? (
                        <select data-testid={`ls-t-boat-${row.row_id}`} value={row.boat_id || ""} disabled={busy}
                          onChange={(e) => { const v = e.target.value; if (v === "__cancel__") onCancel(row); else onAssign(row, Number(v)); }}
                          className="w-full text-[12px] border border-slate-200 rounded px-1 py-1 bg-white text-slate-700">
                          <option value="" disabled>Seç</option>
                          {boatOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                          <option value="__cancel__">❌ İptal</option>
                        </select>
                      ) : <span className="text-[11px] text-slate-400">—</span>}
                    </td>
                    <td className="px-1 py-1 text-center" style={{ minWidth: 74 }}>
                      <button data-testid={`ls-t-checkin-${row.row_id}`} disabled={busy || isCancelled} onClick={() => onCheckin(row)}
                        className={`inline-flex items-center justify-center w-9 h-8 rounded ${row.checked_in ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"} disabled:opacity-40`}>
                        <Check size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
